import { httpsCallable } from 'firebase/functions';
import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  Language,
  TaxonomyMarketAbbreviaton,
} from '../../common/CountryMarketConfig';
import { functions } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { Taxonomies } from '../../types/briefing';
import { useCountryMarketState } from '../CountryMarketController/CountryMarketState';
import { useTaxonomyState } from './TaxonomyState';

/**
 * The time (in *milliseconds*) after which the taxonomies in the Redux Store expire
 *
 * currently 1 day
 */
//                                ms   * s  * m  * h  * d
const TAXONOMIES_CACHE_LIFETIME = 1000 * 60 * 60 * 24 * 1;
/**
 * The time (in *milliseconds*) after which the taxonomies Error in the Redux Store expires
 *
 * currently 2 minutes
 */
const TAXONOMIES_ERROR_LIFETIME = 1000 * 60 * 2;

export const TaxonomyController: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [taxonomyState, { setTaxonomies, setTaxonomiesError }] =
    useTaxonomyState();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const [{ countryMarketConfig }] = useCountryMarketState();
  const [{ user }] = useAuth();

  const loadTaxonomies = useCallback(
    (market: TaxonomyMarketAbbreviaton, language: Language) => {
      httpsCallable(
        functions,
        'getTaxonomies',
      )({
        marketSegment: market,
        locale: language,
      })
        .then(({ data }) => {
          if (data) {
            dispatch(
              setTaxonomies({
                taxonomies: data as Taxonomies,
                market,
                language,
              }),
            );
          }
        })
        .catch((error) => {
          dispatch(
            setTaxonomiesError({
              error: `Error from Server: ${error.code}`,
              market,
              language,
            }),
          );
        });
    },
    [dispatch, setTaxonomies, setTaxonomiesError],
  );

  const evaluateTaxonomies = useCallback(() => {
    if (!countryMarketConfig || !user) return;

    const market = countryMarketConfig.taxonomyMarketAbbreviaton;
    const locale = i18n.language as Language;
    const state = taxonomyState[market]?.[locale];
    const taxonomies = state?.taxonomies;
    const error = state?.error;

    let updatedAtAsDate;
    if (state?.updatedAt) {
      try {
        updatedAtAsDate = new Date(state?.updatedAt);
      } catch (e) {
        updatedAtAsDate = undefined;
      }
    }

    const taxonomiesAvailable =
      !!taxonomies && Object.keys(taxonomies).length > 0;
    const taxonomiesAreOutdated =
      !updatedAtAsDate ||
      new Date().getTime() - updatedAtAsDate.getTime() >
        TAXONOMIES_CACHE_LIFETIME;
    const taxonomiesAreValid = taxonomiesAvailable && !taxonomiesAreOutdated;

    const isInErrorRetention =
      !!error &&
      new Date().getTime() - (updatedAtAsDate || new Date(0)).getTime() <
        TAXONOMIES_ERROR_LIFETIME;

    if (taxonomiesAreValid || isInErrorRetention) return;

    loadTaxonomies(market, locale);
  }, [countryMarketConfig, i18n.language, loadTaxonomies, taxonomyState, user]);

  useEffect(() => {
    evaluateTaxonomies();

    const evaluationInterval = setInterval(
      evaluateTaxonomies,
      Math.min(TAXONOMIES_CACHE_LIFETIME, TAXONOMIES_ERROR_LIFETIME) + 1000,
    );

    return () => clearInterval(evaluationInterval);
  }, [evaluateTaxonomies]);
  return <>{children}</>;
};
